"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, Eraser, GripVertical, Plus, Search, X } from "lucide-react";
import { trackPremiumFeatureBlocked, type PaywallSurface } from '@/lib/analytics/paywall';
import {
  FONT_BASTION,
  PICKER_ACCENT,
  pickerOptionRowHoverBg,
  pickerOptionRowSelectionStyle,
} from "@/lib/bastion-button";
import { getCategoryIcon, getCategoryIconForCategory } from "@/lib/category-icons";
import { filterMetricsForPicker } from "@/lib/metric-picker-visibility";
import {
  metricMatchesPickerSearch,
  resolvePickerMetricLabel,
} from "@/lib/metric-picker-search";
import {
  CATEGORY_ORDER as METRIC_CATEGORY_ORDER,
  HEATMAP_PICKER_CATEGORY_ORDER,
  SUBCATEGORY_ORDER,
  compareMetricFields,
  resolveMetricSubcategory,
} from "@/lib/metrics-taxonomy";
import {
  getStrategyMetricPickerIcon,
  getStrategyPickerSubcategory,
  isStrategyRatingMetric,
  resolveStrategyMetricPickerLabel,
} from "@/lib/strategy-metric-display";
import { HeroPickerPopoverBackdrop } from "@/app/components/HeroPickerPopoverBackdrop";
import {
  popoverEnterStyle,
  useHoverPopoverOpen,
  usePopoverEnterAnimation,
} from "@/app/components/use-hover-popover-open";
import { useHeroPickerPopoverPosition } from "@/app/components/use-hero-picker-popover-position";
import { bindPopoverWheelTrap } from "@/lib/bind-popover-wheel-trap";
import {
  heroPickerTriggerHoverHandlers,
  heroPickerTriggerOpenStyle,
} from "@/app/components/hero-picker-trigger-hover";
import { HEATMAP_CHART_CONTROL_LABEL_COLOR, heatmapToolbarTriggerDims } from "@/app/heatmap/_components/heatmap-secondary-controls";
import {
  createRadarSpacerColumnId,
  isRadarSpacerColumn,
  RADAR_META_ARCHETYPE,
  RADAR_META_PORTFOLIO,
  RADAR_META_PRICE_1Y,
  RADAR_META_SCORE_PROFILE,
  RADAR_META_WATCHLIST,
  RADAR_SPACER_PICKER_KEY,
} from "@/lib/radar-meta-columns";
import {
  createEtfCorrelationMetricKey,
  etfCorrelationAssetName,
  etfCorrelationMetricLabel,
  fetchEtfCorrelationCatalog,
  isEtfCorrelationMetric,
  type EtfCorrelationCatalogItem,
} from "@/lib/etf-correlation";

const hdr = (s: string) =>
  s.replaceAll("/", "/\u200b").replaceAll("+", "+\u200b");

/** Order within General (size/price + tags on Radar; size/price under Financials on Heatmap). */
const GENERAL_SECTION_METRIC_ORDER = [
  RADAR_META_PRICE_1Y,
  RADAR_META_SCORE_PROFILE,
  "marketcap_ltm",
  "priceclose_ltm",
  "Price",
  "shares_ltm",
  RADAR_META_PORTFOLIO,
  RADAR_META_WATCHLIST,
  RADAR_META_ARCHETYPE,
];

/** Size / price / shares — Strategy · General on Radar; Financials · General on Heatmap. */
const GENERAL_IDENTITY_METRICS = new Set([
  "marketcap_ltm",
  "priceclose_ltm",
  "Price",
  "shares_ltm",
]);

function spacerPickerLabel(key: string): string | null {
  if (key === RADAR_SPACER_PICKER_KEY || isRadarSpacerColumn(key)) return "Spacer";
  return null;
}
// Section headings that need more words in the picker than the stored key.
// "ETFs" alone reads like a list of funds, not a correlation metric.
const PICKER_SUBCATEGORY_LABEL: Record<string, string> = {
  ETFs: "Correlation with ETFs · Experimental",
};

// Thin top-level DB categories fold into a parent tab as one subcategory
// (picker layout only — column_meta unchanged). Forecast remaps into
// Financials with statement-shaped blocks (see FORECAST_SUBCATEGORY_REMAP).
const CATEGORY_FOLD: Record<string, { parent: string; subcategory: string }> = {
  Risk: { parent: "Return", subcategory: "Risk" },
  Leverage: { parent: "Quality", subcategory: "Leverage" },
  Dividend: { parent: "Valuation", subcategory: "Dividend" },
};

const CATEGORY_REMAP: Record<string, string> = {
  Other: "Strategy",
  Forecast: "Financials",
};

/**
 * When Forecast folds into Financials, map DB line-item subcategories into
 * statement-shaped blocks so the tab reads LTM → Forecast · Income / Cash /
 * Per-share / … instead of alphabetical EBIT / EBITDA / EPS sections.
 * Margins → Quality, Multiples → Valuation via FORECAST_TAB_OVERRIDE.
 */
const FORECAST_SUBCATEGORY_REMAP: Record<string, string> = {
  Revenue: "Forecast · Income",
  EBITDA: "Forecast · Income",
  EBIT: "Forecast · Income",
  "Net Income": "Forecast · Income",
  "Cash flow": "Forecast · Cash flow",
  "Per-share": "Forecast · Per-share",
  "EPS forecasts": "Forecast · Per-share",
};

/** Forecast DB subcategories that leave Financials for another tab. */
const FORECAST_TAB_OVERRIDE: Record<string, { category: string; subcategory: string }> = {
  Margins: { category: "Quality", subcategory: "Margin outlook" },
  Multiples: { category: "Valuation", subcategory: "Forecast · Multiples" },
};

// Per-metric tab/subcategory overrides (picker layout only — column_meta unchanged)
const METRIC_PICKER_OVERRIDES: Record<string, { category: string; subcategory: string }> = {
  // Size / price / shares = general identity, not statement financials
  marketcap_ltm: { category: "Strategy", subcategory: "General" },
  priceclose_ltm: { category: "Strategy", subcategory: "General" },
  Price: { category: "Strategy", subcategory: "General" },
  shares_ltm: { category: "Strategy", subcategory: "General" },
  // P/E LTM is a multiple, not a Financials statement line
  pe_ltm: { category: "Valuation", subcategory: "Price multiples" },
  [RADAR_META_PORTFOLIO]: { category: "Strategy", subcategory: "General" },
  [RADAR_META_WATCHLIST]: { category: "Strategy", subcategory: "General" },
  [RADAR_META_ARCHETYPE]: { category: "Strategy", subcategory: "General" },
  [RADAR_META_PRICE_1Y]: { category: "Strategy", subcategory: "General" },
  [RADAR_META_SCORE_PROFILE]: { category: "Strategy", subcategory: "General" },
};

function sortGeneralSectionOptions(
  items: { value: string; label: string; icon_name: string | null }[],
) {
  return [...items].sort((a, b) => {
    const ia = GENERAL_SECTION_METRIC_ORDER.indexOf(a.value);
    const ib = GENERAL_SECTION_METRIC_ORDER.indexOf(b.value);
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    }
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });
}

// Fallback free-metric set for callers that don't pass `isMetricFree`. Must
// mirror HEATMAP_FREE_METRICS in `app/heatmap/_lib/metric-report-access`. The
// heatmap passes a list-aware predicate instead (so locks match the actual
// per-list/per-tier payload), but keep this in lockstep for safety.
const FREE_METRICS = new Set([
  "MarketCap", "1weekreturn", "1monthreturn", "3monthreturn", "YTDreturn",
  "1yearreturn", "5yearreturn", "PE", "PEFWD", "PEGFY1", "DivdendYield",
]);

type ColumnMeta = {
  header?: string;
  picker_label?: string;
  selected_label?: string;
  search_aliases?: string[];
  emoji?: string | null;
  icon_name?: string | null;
  category?: string | null;
  subcategory?: string | null;
  direction?: string | null;
  format?: {
    type?: string;
    decimals?: number;
    signed?: boolean;
    zeroAsDash?: boolean;
  };
};

type MetricCategory = "all" | string;

interface HeroMetricPickerProps {
  selectedMetric: string;
  columnMeta: Record<string, ColumnMeta>;
  availableMetrics: string[];
  /** Multi-metric legacy (radar/playbook). Omit when `singleSelect`. */
  customSelected?: string[];
  /** Manually added metrics pinned at top of picker (checkmarks). Defaults to `customSelected`. */
  pinnedMetrics?: string[];
  /** @deprecated Visible table columns — do not use for picker pins; use `pinnedMetrics`. */
  activeMetrics?: string[];
  isPaid: boolean;
  /**
   * Non-premium accessibility predicate, overrides the default {@link FREE_METRICS}
   * membership. The heatmap passes a list-aware check
   * (`isHeatmapMetricInPayload(metric, listAccess)`) so the lock icons match
   * what the server actually serves for the current list + tier.
   */
  isMetricFree?: (metricKey: string) => boolean;
  user: unknown;
  /** PostHog: which page hosts this picker (default heatmap). */
  analyticsSurface?: PaywallSurface;
  listId?: string;
  onSelectMetric: (key: string) => void;
  /** Multi-metric legacy — not used when `singleSelect`. */
  onAddMetric?: (key: string) => void;
  /** Multi-metric: remove from custom selection (toggle off). */
  onRemoveMetric?: (key: string) => void;
  /** Multi-metric: clear all pinned / ADDED columns at once. */
  onClearPinnedMetrics?: () => void;
  /** Multi-metric: reorder ADDED / pinned columns (drag in picker). */
  onReorderPinnedMetrics?: (orderedKeys: string[]) => void;
  onSignUp: () => void;
  onShowPro: () => void;
  etfMode?: boolean;
  /** Heatmap: one active metric only — single checkmark, no customSelected tracking. */
  singleSelect?: boolean;
  /** Block popover and call `onRequireAuth` on trigger click (anonymous heatmap). */
  requireAuth?: boolean;
  onRequireAuth?: () => void;
  /** Desktop: open on trigger hover with smooth enter animation; touch keeps click toggle. */
  openOnHover?: boolean;
  /** "inline" = compact trigger for scatter chart title row (Y vs X). "toolbar" = heatmap toolbar chip. "chartControl" = second-row chart chip (label + value). "headerAdd" = TradingView-style “+” in table header. */
  variant?: "default" | "inline" | "toolbar" | "chartControl" | "headerAdd";
  /** Muted in-chip label when variant is chartControl (e.g. "X axis"). */
  controlLabel?: string;
  /**
   * When true, free users cannot select PRO metrics — `onShowPro` runs and the
   * picker stays open. Default: allow selection (heatmap main metric uses in-page paywall).
   */
  blockProMetricSelection?: boolean;
  /** Hide the PRO badge. Used for signed-out visitors who should not see the label. */
  hideProBadge?: boolean;
  /** Add the dynamic Correlation tab backed by the ETF catalog. */
  enableCorrelationMetrics?: boolean;
}

type GroupedOption = {
  category: string;
  subcategory: string;
  options: { value: string; label: string; icon_name: string | null }[];
  /** When true, skip the uppercase subcategory label (e.g. Spacer before General). */
  hideSubcategoryHeader?: boolean;
};

type MetricOptionItem = { value: string; label: string; icon_name: string | null };

export default function HeroMetricPicker({
  selectedMetric,
  columnMeta,
  availableMetrics,
  customSelected = [],
  pinnedMetrics,
  activeMetrics,
  isPaid,
  isMetricFree,
  user,
  onSelectMetric,
  onAddMetric,
  onRemoveMetric,
  onClearPinnedMetrics,
  onReorderPinnedMetrics,
  onSignUp,
  onShowPro,
  etfMode,
  singleSelect = false,
  analyticsSurface = 'heatmap',
  listId,
  requireAuth = false,
  onRequireAuth,
  openOnHover = false,
  variant = "default",
  controlLabel,
  blockProMetricSelection = false,
  hideProBadge = false,
  enableCorrelationMetrics = false,
}: HeroMetricPickerProps) {
  const {
    open,
    setOpen,
    hoverCapable,
    handleTriggerEnter,
    handleTriggerLeave,
    handlePopoverEnter,
    handlePopoverLeave,
    handleToggleClick: handleHoverToggleClick,
  } = useHoverPopoverOpen(openOnHover);
  const popoverEntered = usePopoverEnterAnimation(open && openOnHover && hoverCapable);
  const [dragPinnedKey, setDragPinnedKey] = useState<string | null>(null);
  const [dragOverPinnedKey, setDragOverPinnedKey] = useState<string | null>(null);
  const skipPinnedClickRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState<MetricCategory>("all");
  const [correlationEtfs, setCorrelationEtfs] = useState<EtfCorrelationCatalogItem[]>([]);
  const [correlationEtfsLoading, setCorrelationEtfsLoading] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { mounted, layout: popoverLayout } = useHeroPickerPopoverPosition(open, triggerRef, popoverRef, false, true);
  const [headerAddTooltip, setHeaderAddTooltip] = useState(false);
  const [headerAddHoverCapable, setHeaderAddHoverCapable] = useState(true);

  useEffect(() => {
    if (variant !== "headerAdd" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setHeaderAddHoverCapable(mq.matches);
    const onChange = () => setHeaderAddHoverCapable(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [variant]);

  useEffect(() => {
    if (open) setHeaderAddTooltip(false);
  }, [open]);

  useEffect(() => {
    if (!enableCorrelationMetrics || correlationEtfs.length > 0) return;
    const abort = new AbortController();
    setCorrelationEtfsLoading(true);
    fetchEtfCorrelationCatalog()
      .then((catalog) => {
        if (!abort.signal.aborted) setCorrelationEtfs([...catalog.values()]);
      })
      .catch((error) => {
        if (!abort.signal.aborted) {
          console.error("[metric-picker] ETF catalog failed:", error);
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setCorrelationEtfsLoading(false);
      });
    return () => abort.abort();
  }, [enableCorrelationMetrics, correlationEtfs.length]);

  const pickerColumnMeta = useMemo<Record<string, ColumnMeta>>(() => {
    if (!enableCorrelationMetrics || correlationEtfs.length === 0) return columnMeta;
    const dynamic: Record<string, ColumnMeta> = {};
    for (const etf of correlationEtfs) {
      const key = createEtfCorrelationMetricKey(etf.fullTicker);
      const label = `${etfCorrelationAssetName(etf.name)} correlation`;
      dynamic[key] = {
        header: label,
        picker_label: `${etf.ticker} · ${etf.name}`,
        selected_label: label,
        search_aliases: [etf.fullTicker, etf.ticker, etf.name],
        icon_name: "Activity",
        category: "Correlation",
        subcategory: "ETFs",
        direction: "higher_better",
        format: { type: "ratio", decimals: 2 },
      };
    }
    return { ...columnMeta, ...dynamic };
  }, [columnMeta, correlationEtfs, enableCorrelationMetrics]);

  const pickerAvailableMetrics = useMemo(() => {
    if (!enableCorrelationMetrics || correlationEtfs.length === 0) return availableMetrics;
    return [
      ...new Set([
        ...availableMetrics,
        ...correlationEtfs.map((etf) => createEtfCorrelationMetricKey(etf.fullTicker)),
      ]),
    ];
  }, [availableMetrics, correlationEtfs, enableCorrelationMetrics]);

  const pinnedMetricsList = pinnedMetrics ?? customSelected;
  const selectedMetrics = activeMetrics ?? pinnedMetricsList;

  const selectedHeader = useMemo(() => {
    if (variant === "headerAdd") return "Add column";
    if (!singleSelect && variant === "toolbar") {
      if (pinnedMetricsList.length > 0) {
        return `Metric + (${pinnedMetricsList.length})`;
      }
      return "Metric +";
    }
    if (!selectedMetric) return "Select metric";
    const meta = pickerColumnMeta[selectedMetric];
    if (meta?.selected_label) return hdr(meta.selected_label);
    if (isEtfCorrelationMetric(selectedMetric)) {
      return hdr(etfCorrelationMetricLabel(selectedMetric));
    }
    const strategyLabel = resolveStrategyMetricPickerLabel(
      selectedMetric,
      meta?.header ? String(meta.header) : null,
    );
    const label = resolvePickerMetricLabel(selectedMetric, strategyLabel);
    return hdr(label);
  }, [selectedMetric, pickerColumnMeta, singleSelect, variant, pinnedMetricsList.length]);

  const SelectedIcon = useMemo(() => {
    if (!selectedMetric) return null;
    const strategyIcon = getStrategyMetricPickerIcon(selectedMetric);
    if (strategyIcon) return strategyIcon;
    const meta = pickerColumnMeta[selectedMetric];
    return getCategoryIcon(meta?.icon_name) ?? getCategoryIconForCategory((meta?.category && String(meta.category).trim()) || "Strategy");
  }, [selectedMetric, pickerColumnMeta]);

  // Build grouped options from availableMetrics + columnMeta
  const grouped = useMemo<GroupedOption[]>(() => {
    const pickerMetrics = filterMetricsForPicker(pickerAvailableMetrics, pickerColumnMeta);
    const q = searchTerm.trim().toLowerCase();
    const filtered = q
      ? pickerMetrics.filter((k) => {
          const header = pickerColumnMeta[k]?.header;
          const strategyLabel = resolveStrategyMetricPickerLabel(k, header ? String(header) : null);
          return metricMatchesPickerSearch(k, {
            query: q,
            header,
            category: pickerColumnMeta[k]?.category,
            extraLabels: [strategyLabel, ...(pickerColumnMeta[k]?.search_aliases ?? [])],
          });
        })
      : pickerMetrics;

    const mapped = filtered.map((metric) => {
      const meta = pickerColumnMeta[metric];
      const rawHeader =
        meta?.picker_label && String(meta.picker_label).trim()
          ? String(meta.picker_label).trim()
          : meta?.header && String(meta.header).trim()
            ? String(meta.header).trim()
            : metric;
      const strategyLabel = resolveStrategyMetricPickerLabel(metric, rawHeader);
      const header = hdr(resolvePickerMetricLabel(metric, strategyLabel));
      const icon_name = meta?.icon_name ?? null;
      const rawCategory = (meta?.category && String(meta.category).trim()) || "Other";
      const baseOverride = METRIC_PICKER_OVERRIDES[metric];
      const strategyOverride = isStrategyRatingMetric(metric)
        ? { category: "Strategy", subcategory: getStrategyPickerSubcategory(metric) }
        : undefined;
      // Heatmap: General identity metrics live under Financials (not Strategy).
      const heatmapGeneralOverride =
        singleSelect && GENERAL_IDENTITY_METRICS.has(metric)
          ? { category: "Financials", subcategory: "General" }
          : undefined;
      const fold = CATEGORY_FOLD[rawCategory];
      const override = heatmapGeneralOverride ?? baseOverride ?? strategyOverride;
      const dbSub = resolveMetricSubcategory(metric, meta);
      const forecastTab =
        !override && !fold && rawCategory === "Forecast" && dbSub
          ? FORECAST_TAB_OVERRIDE[dbSub]
          : undefined;
      const category =
        override?.category ??
        fold?.parent ??
        forecastTab?.category ??
        CATEGORY_REMAP[rawCategory] ??
        rawCategory;
      let subcategory =
        override?.subcategory ??
        fold?.subcategory ??
        forecastTab?.subcategory ??
        dbSub ??
        rawCategory;
      // Forecast → Financials: collapse line-item DB subs into statement blocks.
      if (
        !override?.subcategory &&
        !fold?.subcategory &&
        !forecastTab &&
        rawCategory === "Forecast" &&
        category === "Financials"
      ) {
        subcategory = FORECAST_SUBCATEGORY_REMAP[subcategory] ?? subcategory;
      }
      return { value: metric, label: header, icon_name, category, subcategory };
    });

    // Group by [parentTab, subcategory]
    const bySub = new Map<string, Map<string, { value: string; label: string; icon_name: string | null }[]>>();
    for (const m of mapped) {
      if (!bySub.has(m.category)) bySub.set(m.category, new Map());
      const catMap = bySub.get(m.category)!;
      if (!catMap.has(m.subcategory)) catMap.set(m.subcategory, []);
      catMap.get(m.subcategory)!.push({ value: m.value, label: m.label, icon_name: m.icon_name });
    }

    const categoryOrder = singleSelect ? HEATMAP_PICKER_CATEGORY_ORDER : METRIC_CATEGORY_ORDER;
    const tabOrder = Array.from(bySub.keys()).sort((a, b) => {
      // Experimental ETF correlation always goes last, in every picker.
      if (a === "Correlation" || b === "Correlation") return a === "Correlation" ? 1 : b === "Correlation" ? -1 : 0;
      const ia = categoryOrder.indexOf(a);
      const ib = categoryOrder.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b, undefined, { sensitivity: "base" });
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

    const result: GroupedOption[] = [];
    for (const tab of tabOrder) {
      const catMap = bySub.get(tab)!;
      const subOrder = SUBCATEGORY_ORDER[tab];
      const orderedSubs = subOrder ? subOrder.filter((s) => catMap.has(s)) : [];
      const extraSubs = subOrder
        ? Array.from(catMap.keys())
            .filter((s) => !subOrder.includes(s))
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        : [];
      const subs = subOrder
        ? [...orderedSubs, ...extraSubs]
        : Array.from(catMap.keys()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

      for (const sub of subs) {
        if (sub === "Other" && !q) continue;
        const items = catMap.get(sub) || [];
        if (items.length === 0) continue;
        const sorted =
          sub === "General" && (tab === "Strategy" || tab === "Financials")
            ? sortGeneralSectionOptions(items)
            : items.sort((a, b) => compareMetricFields(a.value, a.label, b.value, b.label));
        result.push({
          category: tab,
          subcategory: sub,
          options: sorted,
        });
      }
    }
    return result;
  }, [pickerAvailableMetrics, searchTerm, pickerColumnMeta, singleSelect]);

  const visibleCategories = useMemo(() => {
    const seen = new Set<string>();
    const all = grouped.reduce<string[]>((acc, g) => {
      if (!seen.has(g.category)) { seen.add(g.category); acc.push(g.category); }
      return acc;
    }, []);
    const base = etfMode
      ? all.filter(c => c === "Return" || c === "Correlation")
      : all;
    if (enableCorrelationMetrics && !base.includes("Correlation")) {
      base.push("Correlation");
    }
    return base;
  }, [grouped, etfMode, enableCorrelationMetrics]);

  const filteredGroups = useMemo(() => {
    let base = grouped;
    if (etfMode) {
      base = base.filter(
        (g) => g.category === "Return" || g.category === "Correlation",
      );
    }
    if (activeCategory !== "all") {
      base = base.filter((g) => g.category === activeCategory);
    }

    // Strip Layout-only Spacer group; catalog key is injected below (Radar only).
    const withoutLayout: GroupedOption[] = [];
    let hadCatalogSpacer = false;
    for (const g of base) {
      if (g.category === "Strategy" && g.subcategory === "Layout") {
        if (g.options.some((o) => o.value === RADAR_SPACER_PICKER_KEY)) {
          hadCatalogSpacer = true;
        }
        const rest = g.options.filter((o) => o.value !== RADAR_SPACER_PICKER_KEY);
        if (rest.length > 0) withoutLayout.push({ ...g, options: rest });
        continue;
      }
      withoutLayout.push({
        ...g,
        options: g.options.filter((o) => o.value !== RADAR_SPACER_PICKER_KEY),
      });
    }

    const groups = withoutLayout.filter((g) => g.options.length > 0);

    const withGeneralFirst = (list: GroupedOption[]): GroupedOption[] => {
      // Radar only: pin Strategy · General near the top of All (after Spacer).
      // Heatmap keeps Financials · General in Financials category order.
      if (activeCategory !== "all" || singleSelect) return list;
      const generalIdx = list.findIndex(
        (g) => g.category === "Strategy" && g.subcategory === "General",
      );
      if (generalIdx === -1) return list;
      const generalGroup = list[generalIdx];
      const rest = list.filter((_, i) => i !== generalIdx);
      return [
        {
          ...generalGroup,
          options: sortGeneralSectionOptions(generalGroup.options),
        },
        ...rest,
      ];
    };

    // Heatmap / single-metric pickers: no column spacers.
    if (singleSelect) {
      return withGeneralFirst(groups);
    }

    const spacerOpt: MetricOptionItem = {
      value: RADAR_SPACER_PICKER_KEY,
      label: "Spacer",
      icon_name: "Minus",
    };

    // All: one Spacer above General (same placement as before).
    if (activeCategory === "all") {
      const withSpacer: GroupedOption[] =
        hadCatalogSpacer || groups.length > 0
          ? [
              {
                category: "Strategy",
                subcategory: "",
                options: [spacerOpt],
                hideSubcategoryHeader: true,
              },
            ]
          : [];
      return [...withSpacer, ...withGeneralFirst(groups)];
    }

    // Category tabs (Strategy / Valuation / …): one Spacer as the first
    // row of the tab, before any subcategory headers — same as All.
    if (groups.length === 0 && !hadCatalogSpacer) return groups;
    return [
      {
        category: activeCategory,
        subcategory: "",
        options: [spacerOpt],
        hideSubcategoryHeader: true,
      },
      ...groups,
    ];
  }, [grouped, activeCategory, etfMode, singleSelect]);

  const optionByValue = useMemo(() => {
    const map = new Map<string, MetricOptionItem>();
    for (const g of grouped) {
      for (const o of g.options) map.set(o.value, o);
    }
    return map;
  }, [grouped]);

  const pinnedSelectedOptions = useMemo((): MetricOptionItem[] => {
    if (singleSelect || pinnedMetricsList.length === 0) return [];
    return pinnedMetricsList.map((key) => {
      const found = optionByValue.get(key);
      if (found) return found;
      const spacerLabel = spacerPickerLabel(key);
      if (spacerLabel) {
        return { value: key, label: spacerLabel, icon_name: "Minus" };
      }
      const meta = pickerColumnMeta[key];
      const rawHeader = meta?.header && String(meta.header).trim() ? String(meta.header).trim() : key;
      return {
        value: key,
        label: hdr(resolveStrategyMetricPickerLabel(key, rawHeader)),
        icon_name: meta?.icon_name ?? null,
      };
    });
  }, [singleSelect, pinnedMetricsList, optionByValue, pickerColumnMeta]);

  const reorderPinnedKeys = useCallback(
    (fromKey: string, toKey: string) => {
      if (!onReorderPinnedMetrics || fromKey === toKey) return;
      const keys = pinnedSelectedOptions.map((o) => o.value);
      const fromIdx = keys.indexOf(fromKey);
      const toIdx = keys.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = [...keys];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      onReorderPinnedMetrics(next);
    },
    [onReorderPinnedMetrics, pinnedSelectedOptions],
  );

  const browseGroups = useMemo(() => {
    if (singleSelect) return filteredGroups;
    return filteredGroups
      .map((g) => ({
        ...g,
        // Keep Spacer catalog key visible so users can add multiple spacers.
        options: g.options.filter(
          (o) => o.value === RADAR_SPACER_PICKER_KEY || !pinnedMetricsList.includes(o.value),
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [filteredGroups, pinnedMetricsList, singleSelect]);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(t) &&
        triggerRef.current && !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Prevent scroll propagation
  useEffect(() => {
    if (!open || !popoverLayout) return;
    const el = popoverRef.current;
    if (!el) return;
    return bindPopoverWheelTrap(el);
  }, [open, popoverLayout]);

  const metricIsFree = useCallback(
    (key: string) => {
      if (key === RADAR_SPACER_PICKER_KEY || isRadarSpacerColumn(key)) return true;
      if (isEtfCorrelationMetric(key)) return false;
      return isMetricFree ? isMetricFree(key) : FREE_METRICS.has(key);
    },
    [isMetricFree],
  );

  const handleMetricClick = useCallback((metricKey: string) => {
    // Anonymous users hit the AuthModal first — consistent with the
    // rest of /heatmap's controls. For signed-in non-premium users the
    // page's in-page paywall (blurred preview + UpgradeBanner) handles
    // the upsell after selection unless `blockProMetricSelection` is set.
    if (!user) {
      onSignUp();
      return;
    }
    // Spacer: always insert a new instance (catalog key stays available).
    if (metricKey === RADAR_SPACER_PICKER_KEY) {
      onAddMetric?.(createRadarSpacerColumnId());
      return;
    }
    const isFree = metricIsFree(metricKey);
    if (!isFree && !isPaid) {
      trackPremiumFeatureBlocked({
        surface: analyticsSurface,
        trigger: 'metric_gate',
        action: 'metric_selected',
        metric: metricKey,
        list_id: listId,
        is_authenticated: true,
      });
      if (blockProMetricSelection) {
        onShowPro();
        return;
      }
    }
    if (!singleSelect) {
      if (pinnedMetricsList.includes(metricKey)) {
        onRemoveMetric?.(metricKey);
      } else {
        onAddMetric?.(metricKey);
      }
      onSelectMetric(metricKey);
      return;
    }
    onSelectMetric(metricKey);
    if (singleSelect) {
      setSearchTerm("");
      setOpen(false);
    }
  }, [
    user,
    isPaid,
    metricIsFree,
    analyticsSurface,
    listId,
    onSignUp,
    onAddMetric,
    onRemoveMetric,
    onSelectMetric,
    singleSelect,
    pinnedMetricsList,
    blockProMetricSelection,
    onShowPro,
  ]);

  const handleTriggerClick = useCallback(() => {
    if (requireAuth) {
      onRequireAuth?.();
      return;
    }
    if (openOnHover) {
      handleHoverToggleClick();
      return;
    }
    setOpen((v) => !v);
  }, [requireAuth, onRequireAuth, openOnHover, handleHoverToggleClick, setOpen]);

  const isInline = variant === "inline";
  const isToolbar = variant === "toolbar";
  const isChartControl = variant === "chartControl";
  const isHeaderAdd = variant === "headerAdd";

  return (
    <div style={{ position: "relative", display: isHeaderAdd ? "block" : "inline-block", width: isHeaderAdd ? "100%" : undefined, height: isHeaderAdd ? "100%" : undefined }}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-haspopup={requireAuth ? undefined : "listbox"}
        aria-expanded={requireAuth ? undefined : open}
        aria-label={isHeaderAdd ? "Add column" : undefined}
        style={
          isHeaderAdd
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: 42,
                padding: 0,
                border: "none",
                background: open ? "rgba(255,255,255,0.08)" : "transparent",
                color: open ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.45)",
                cursor: "pointer",
                transition: "background 0.12s, color 0.12s",
              }
            : isInline
            ? {
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 6px",
                borderRadius: 4,
                ...heroPickerTriggerOpenStyle("inline", open),
                border: "none",
                color: "#ffffff",
                fontFamily: "Arial, Helvetica, sans-serif",
                fontWeight: "bold",
                fontSize: 14,
                letterSpacing: "0.3px",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }
            : isChartControl
              ? {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  height: 26,
                  boxSizing: "border-box",
                  padding: "0 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.06)",
                  ...heroPickerTriggerOpenStyle("medium", open),
                  fontFamily: FONT_BASTION,
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition:
                    "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                }
            : isToolbar
              ? {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  ...heatmapToolbarTriggerDims(),
                  ...heroPickerTriggerOpenStyle("medium", open),
                  color: "#fff",
                  fontFamily: FONT_BASTION,
                  letterSpacing: "-0.01em",
                  cursor: "pointer",
                  transition: "background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                }
              : {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px 6px 12px",
                minHeight: 36,
                borderRadius: 10,
                ...heroPickerTriggerOpenStyle("medium", open),
                color: "#fff",
                fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                cursor: "pointer",
                transition: "background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
              }
        }
        {...(isHeaderAdd ? {} : heroPickerTriggerHoverHandlers(
          isInline ? "inline" : "medium",
          open,
        ))}
        {...(isHeaderAdd ? {
          onMouseEnter: (e: MouseEvent<HTMLButtonElement>) => {
            if (!open) {
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
              e.currentTarget.style.color = "rgba(255,255,255,0.75)";
            }
            if (headerAddHoverCapable && !open) setHeaderAddTooltip(true);
          },
          onMouseLeave: (e: MouseEvent<HTMLButtonElement>) => {
            if (!open) {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "rgba(255,255,255,0.45)";
            }
            setHeaderAddTooltip(false);
          },
        } : {})}
      >
        {isHeaderAdd ? (
          <Plus size={16} strokeWidth={1.75} />
        ) : isChartControl ? (
          <>
            {controlLabel ? (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1,
                  color: HEATMAP_CHART_CONTROL_LABEL_COLOR,
                  flexShrink: 0,
                }}
              >
                {controlLabel}
              </span>
            ) : null}
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1,
                color: open ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.72)",
                minWidth: 0,
              }}
            >
              {selectedHeader}
            </span>
            <ChevronDown
              size={12}
              strokeWidth={2}
              style={{
                flexShrink: 0,
                opacity: open ? 0.85 : 0.5,
                transition:
                  "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </>
        ) : (
          <>
        {SelectedIcon && (singleSelect || variant !== "toolbar") && (
          <SelectedIcon
            size={isInline ? 14 : isToolbar ? 14 : 16}
            strokeWidth={1.6}
            style={{
              color: "rgba(255,255,255,0.5)",
              flexShrink: 0,
            }}
          />
        )}
        <span
          style={
            isInline
              ? {
                  fontSize: 14,
                  fontWeight: "bold",
                  color: selectedMetric ? "#fff" : "rgba(255,255,255,0.38)",
                  lineHeight: 1,
                }
              : {
                  fontSize: 13,
                  fontWeight: 500,
                  color: selectedMetric ? "#fff" : "rgba(255,255,255,0.38)",
                  letterSpacing: "-0.01em",
                  lineHeight: 1,
                }
          }
        >
          {selectedHeader}
        </span>
        {isInline ? (
          <span style={{ fontSize: 10, opacity: 0.5 }}>▼</span>
        ) : (
          <ChevronCaret open={open} />
        )}
          </>
        )}
      </button>

      {isHeaderAdd ? (
        <MetricHeaderTooltip
          triggerRef={triggerRef}
          label="Add column"
          visible={headerAddTooltip && headerAddHoverCapable && !open}
        />
      ) : null}

      {/* Popover — portaled so parent overflow:hidden (e.g. heatmap hero) cannot clip it */}
      {!requireAuth && mounted && open && popoverLayout && createPortal(
        <>
          {popoverLayout.isMobile && <HeroPickerPopoverBackdrop onClose={() => setOpen(false)} />}
          <div
          ref={popoverRef}
          role="listbox"
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          style={{
            position: popoverLayout.position,
            top: popoverLayout.top,
            left: popoverLayout.left,
            width: popoverLayout.width,
            maxHeight: popoverLayout.maxHeight,
            height: popoverLayout.maxHeight,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            border: "1px solid #444",
            borderRadius: 12,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            fontFamily: FONT_BASTION,
            ...(openOnHover && hoverCapable ? popoverEnterStyle(popoverEntered) : {}),
          }}
        >
          {/* Search */}
          <div style={{
            padding: popoverLayout.isMobile ? "12px 12px 10px 12px" : "20px 20px 16px 20px",
            borderBottom: "1px solid #333",
            backgroundColor: "#1a1a1a",
            flexShrink: 0,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: "#2a2a2a",
              border: "1px solid #555",
              borderRadius: 8,
              padding: "10px 14px",
            }}>
              <Search size={13} style={{ color: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeCategory === "Correlation" ? "Search ETFs…" : "Search metrics…"}
                autoFocus
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "#fff",
                  fontSize: 13,
                  fontFamily: FONT_BASTION,
                }}
              />
              <button
                onClick={() => {
                  if (searchTerm) setSearchTerm("");
                  else setOpen(false);
                }}
                aria-label="Close"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  border: "none",
                  background: "transparent",
                  color: "rgba(255,255,255,0.35)",
                  cursor: "pointer",
                  flexShrink: 0,
                  padding: 0,
                  transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "rgba(255,255,255,0.35)";
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>

            {/* Category tabs */}
            <MetricTabsRow
              categories={visibleCategories}
              activeCategory={activeCategory}
              onSelect={setActiveCategory}
            />
          </div>

          {/* Body — scrollable */}
          <div
            className="custom-dropdown-scrollbar"
            style={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              scrollbarWidth: "thin",
              scrollbarColor: "#3d3d3d #1a1a1a",
            }}
          >
            {activeCategory === "Correlation" && correlationEtfsLoading ? (
              <div style={{
                padding: "16px 14px",
                color: "rgba(255,255,255,0.4)",
                fontSize: 12,
                textAlign: "center",
              }}>
                Loading ETFs…
              </div>
            ) : browseGroups.length === 0 && pinnedSelectedOptions.length === 0 ? (
              <div style={{
                padding: "16px 14px",
                color: "rgba(255,255,255,0.4)",
                fontSize: 12,
                textAlign: "center",
              }}>
                No matching metrics
              </div>
            ) : (
              <>
                {!singleSelect && pinnedSelectedOptions.length > 0 ? (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "10px 14px 6px",
                        minHeight: 28,
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: 22,
                          fontSize: 9.5,
                          fontWeight: 600,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "rgba(255,255,255,0.35)",
                          lineHeight: 1,
                          fontFamily: FONT_BASTION,
                        }}
                      >
                        Added
                      </div>
                      {onClearPinnedMetrics ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClearPinnedMetrics();
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                            height: 22,
                            boxSizing: "border-box",
                            border: "1px solid rgba(248,113,113,0.28)",
                            background: "rgba(248,113,113,0.08)",
                            color: "#f87171",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            cursor: "pointer",
                            padding: "0 7px",
                            borderRadius: 4,
                            fontFamily: FONT_BASTION,
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(248,113,113,0.16)";
                            e.currentTarget.style.borderColor = "rgba(248,113,113,0.45)";
                            e.currentTarget.style.color = "#fca5a5";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(248,113,113,0.08)";
                            e.currentTarget.style.borderColor = "rgba(248,113,113,0.28)";
                            e.currentTarget.style.color = "#f87171";
                          }}
                        >
                          <Eraser size={11} strokeWidth={2.2} />
                          Clear
                        </button>
                      ) : null}
                    </div>
                    {pinnedSelectedOptions.map((opt) => {
                      const isDragging = dragPinnedKey === opt.value;
                      const isDropTarget =
                        dragOverPinnedKey === opt.value && dragPinnedKey !== opt.value;
                      return (
                        <MetricPickerOptionRow
                          key={`pinned:${opt.value}`}
                          opt={opt}
                          isActive={selectedMetric === opt.value}
                          isPinned
                          isCustomSelected
                          isPaid={isPaid}
                          isFree={metricIsFree(opt.value)}
                          hideProBadge={hideProBadge}
                          draggable={Boolean(onReorderPinnedMetrics)}
                          isDragging={isDragging}
                          isDropTarget={isDropTarget}
                          onDragStart={() => {
                            setDragPinnedKey(opt.value);
                            skipPinnedClickRef.current = false;
                          }}
                          onDragOver={() => {
                            if (dragPinnedKey && dragPinnedKey !== opt.value) {
                              setDragOverPinnedKey(opt.value);
                            }
                          }}
                          onDragLeave={() => {
                            setDragOverPinnedKey((cur) => (cur === opt.value ? null : cur));
                          }}
                          onDrop={() => {
                            if (dragPinnedKey) {
                              skipPinnedClickRef.current = true;
                              reorderPinnedKeys(dragPinnedKey, opt.value);
                            }
                            setDragPinnedKey(null);
                            setDragOverPinnedKey(null);
                          }}
                          onDragEnd={() => {
                            setDragPinnedKey(null);
                            setDragOverPinnedKey(null);
                            window.setTimeout(() => {
                              skipPinnedClickRef.current = false;
                            }, 0);
                          }}
                          onClick={() => {
                            if (skipPinnedClickRef.current) {
                              skipPinnedClickRef.current = false;
                              return;
                            }
                            handleMetricClick(opt.value);
                          }}
                        />
                      );
                    })}
                    <div
                      aria-hidden
                      style={{
                        height: 1,
                        margin: "8px 14px 4px",
                        background: "linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent)",
                      }}
                    />
                  </div>
                ) : null}
                {browseGroups.map((group) => (
                  <div key={`${group.category}-${group.subcategory || "flat"}-${group.options.map((o) => o.value).join(",")}`}>
                    {!group.hideSubcategoryHeader && group.subcategory ? (
                      <div style={{
                        padding: "12px 14px 4px",
                        fontSize: 9.5,
                        fontWeight: 600,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.35)",
                      }}>
                        {PICKER_SUBCATEGORY_LABEL[group.subcategory] ?? group.subcategory}
                      </div>
                    ) : group.hideSubcategoryHeader ? (
                      <div style={{ height: 6 }} />
                    ) : null}
                    {group.options.map((opt) => (
                      <MetricPickerOptionRow
                        key={opt.value}
                        opt={opt}
                        isActive={selectedMetric === opt.value}
                        isPinned={false}
                        isCustomSelected={pinnedMetricsList.includes(opt.value)}
                        isPaid={isPaid}
                        isFree={metricIsFree(opt.value)}
                        hideProBadge={hideProBadge}
                        onClick={() => handleMetricClick(opt.value)}
                      />
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        </>,
        document.body,
      )}
    </div>
  );
}

/* ─────────────────────────── Helpers ─────────────────────────── */

const METRIC_HEADER_TOOLTIP_GAP = 10;

function MetricHeaderTooltip({
  triggerRef,
  label,
  visible,
}: {
  triggerRef: React.RefObject<HTMLElement | null>;
  label: string;
  visible: boolean;
}) {
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!visible || !triggerRef.current) return;
    const reposition = () => {
      const anchor = triggerRef.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      setPos({ x: r.left + r.width / 2, y: r.top - METRIC_HEADER_TOOLTIP_GAP });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [visible, triggerRef]);

  if (!visible || typeof document === "undefined") return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        transform: "translate(-50%, -100%)",
        whiteSpace: "nowrap",
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.3,
        color: "rgba(255,255,255,0.92)",
        background: "#2a2a2a",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 6,
        padding: "6px 10px 8px",
        pointerEvents: "none",
        zIndex: 9999,
        boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
      }}
    >
      <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{label}</span>
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          bottom: -5,
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderTop: "5px solid #2a2a2a",
        }}
      />
    </div>,
    document.body,
  );
}

function MetricPickerOptionRow({
  opt,
  isActive,
  isPinned,
  isCustomSelected,
  isPaid,
  isFree,
  hideProBadge = false,
  onClick,
  draggable = false,
  isDragging = false,
  isDropTarget = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  opt: MetricOptionItem;
  isActive: boolean;
  isPinned: boolean;
  isCustomSelected: boolean;
  isPaid: boolean;
  isFree: boolean;
  hideProBadge?: boolean;
  onClick: () => void;
  draggable?: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  onDragStart?: () => void;
  onDragOver?: () => void;
  onDragLeave?: () => void;
  onDrop?: () => void;
  onDragEnd?: () => void;
}) {
  const highlighted = isActive || isCustomSelected;
  const selectionStyle = pickerOptionRowSelectionStyle(highlighted);
  const baseBg = isDropTarget
    ? "rgba(220,226,236,0.12)"
    : selectionStyle.backgroundColor;
  return (
    <button
      type="button"
      onClick={onClick}
      role="option"
      aria-selected={highlighted}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", opt.value);
        onDragStart?.();
      }}
      onDragOver={(e) => {
        if (!draggable) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver?.();
      }}
      onDragLeave={() => {
        if (!draggable) return;
        onDragLeave?.();
      }}
      onDrop={(e) => {
        if (!draggable) return;
        e.preventDefault();
        e.stopPropagation();
        onDrop?.();
      }}
      onDragEnd={() => {
        if (!draggable) return;
        onDragEnd?.();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "8px 12px",
        border: "none",
        ...selectionStyle,
        backgroundColor: baseBg,
        opacity: isDragging ? 0.45 : 1,
        boxShadow: isDropTarget
          ? `${selectionStyle.boxShadow ?? "inset 2px 0 0 transparent"}, inset 0 0 0 1px rgba(220,226,236,0.35)`
          : selectionStyle.boxShadow,
        cursor: draggable ? "grab" : "pointer",
        fontSize: 12,
        textAlign: "left",
        transition: "background-color 0.2s ease, color 0.2s ease, opacity 0.15s ease",
        fontFamily: FONT_BASTION,
      }}
      onMouseEnter={(e) => {
        if (isDragging || isDropTarget) return;
        e.currentTarget.style.backgroundColor = pickerOptionRowHoverBg(highlighted, "enter");
      }}
      onMouseLeave={(e) => {
        if (isDragging || isDropTarget) return;
        e.currentTarget.style.backgroundColor = pickerOptionRowHoverBg(highlighted, "leave");
      }}
    >
      {draggable ? (
        <span
          aria-hidden
          style={{
            width: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: "rgba(255,255,255,0.28)",
            marginLeft: -2,
          }}
        >
          <GripVertical size={12} strokeWidth={2} />
        </span>
      ) : null}
      {(() => {
        const StrategyIcon = getStrategyMetricPickerIcon(opt.value);
        const OptIcon = StrategyIcon ?? getCategoryIcon(opt.icon_name);
        if (!OptIcon) return null;
        return (
          <span style={{ width: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <OptIcon size={13} strokeWidth={1.6} style={{ color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />
          </span>
        );
      })()}
      <span style={{
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: highlighted ? 600 : 400,
      }}>
        {opt.label}
      </span>
      {!isFree && !isPaid && !hideProBadge && (
        <span
          style={{
            fontFamily: 'var(--font-jetbrains), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: 8.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "#DCE2EC",
            background: "rgba(220,226,236,0.10)",
            border: "1px solid rgba(220,226,236,0.22)",
            borderRadius: 4,
            padding: "1px 4px",
            lineHeight: 1.2,
            flexShrink: 0,
          }}
        >
          PRO
        </span>
      )}
      {isPinned && (isPaid || isFree) ? (
        <svg style={{ width: 14, height: 14, flexShrink: 0, color: PICKER_ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : null}
      {!isPinned && isActive && (isPaid || isFree) ? (
        <svg style={{ width: 14, height: 14, flexShrink: 0, color: PICKER_ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : null}
    </button>
  );
}

function ChevronCaret({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      style={{
        marginLeft: 2,
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.18s",
        color: "rgba(255,255,255,0.55)",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <path d="M2 3.5 L5 6.5 L8 3.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MetricTabsRow({
  categories,
  activeCategory,
  onSelect,
}: {
  categories: string[];
  activeCategory: MetricCategory;
  onSelect: (key: MetricCategory) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const dragState = useRef({ dragging: false, startX: 0, startScroll: 0, moved: false });

  const check = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    check();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, [check]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ds = dragState.current;
    const onDown = (e: PointerEvent) => {
      ds.dragging = true; ds.startX = e.clientX; ds.startScroll = el.scrollLeft; ds.moved = false;
      el.style.cursor = "grabbing"; el.style.userSelect = "none";
    };
    const onMove = (e: PointerEvent) => {
      if (!ds.dragging) return;
      const dx = e.clientX - ds.startX;
      if (Math.abs(dx) > 3) ds.moved = true;
      el.scrollLeft = ds.startScroll - dx;
    };
    const onUp = () => { ds.dragging = false; el.style.cursor = "grab"; el.style.userSelect = ""; };
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const scroll = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: "smooth" });
  };

  const mask =
    canLeft && canRight ? "linear-gradient(to right, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)"
    : canRight ? "linear-gradient(to right, black calc(100% - 20px), transparent 100%)"
    : canLeft ? "linear-gradient(to right, transparent 0%, black 20px)"
    : "none";

  const allTabs: { key: MetricCategory; label: string }[] = [
    { key: "all", label: "All" },
    ...categories.map((c) => ({ key: c, label: c })),
  ];

  return (
    <div style={{ position: "relative", marginTop: 12 }}>
      {canLeft && (
        <button
          onClick={() => scroll(-1)}
          style={{
            position: "absolute", left: -2, top: "50%", transform: "translateY(-50%)",
            width: 18, height: 18, borderRadius: "50%", border: "1px solid #555",
            background: "#2a2a2a", color: "rgba(255,255,255,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0, zIndex: 2, transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#444"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#2a2a2a"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
        >
          <ChevronRight size={11} style={{ transform: "rotate(180deg)" }} />
        </button>
      )}
      {canRight && (
        <button
          onClick={() => scroll(1)}
          style={{
            position: "absolute", right: -2, top: "50%", transform: "translateY(-50%)",
            width: 18, height: 18, borderRadius: "50%", border: "1px solid #555",
            background: "#2a2a2a", color: "rgba(255,255,255,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0, zIndex: 2, transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#444"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#2a2a2a"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
        >
          <ChevronRight size={11} />
        </button>
      )}
      <div
        ref={scrollRef}
        className="metric-tabs-row"
        style={{
          display: "flex",
          gap: 4,
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          maskImage: mask,
          WebkitMaskImage: mask,
          cursor: "grab",
          touchAction: "pan-x",
        }}
      >
        <style>{`.metric-tabs-row::-webkit-scrollbar { display: none; }`}</style>
        {allTabs.map((tab) => {
          const active = activeCategory === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={(e) => {
                if (dragState.current.moved) { e.preventDefault(); return; }
                e.stopPropagation();
                onSelect(tab.key);
              }}
              aria-label={tab.label || undefined}
              title={tab.label || undefined}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 500,
                fontFamily: FONT_BASTION,
                color: active ? "#fff" : "rgba(255,255,255,0.5)",
                backgroundColor: active ? "#333" : "transparent",
                border: active ? "1px solid #555" : "1px solid transparent",
                borderRadius: 8,
                cursor: "pointer",
                transition: "all 0.15s ease",
                outline: "none",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = "#2a2a2a";
                  e.currentTarget.style.borderColor = "#444";
                  e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                  e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                }
              }}
            >
              {(() => {
                if (tab.key === "all") return null;
                const TabIcon = getCategoryIconForCategory(tab.key);
                if (!TabIcon) return null;
                return <TabIcon size={13} strokeWidth={1.8} style={{ color: active ? "#fff" : "rgba(255,255,255,0.45)", marginRight: 5 }} />;
              })()}
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
